<template>
  <GlobalWindow :title="title" :visible.sync="visible" :confirm-working="isWorking" width="80%" @confirm="handleConfirm">
    <el-form :model="form" ref="form" :rules="rules" label-width="100px" v-if="form && form.layout">
      <!-- ====== 基本信息 ====== -->
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="模板名称" prop="name"><el-input v-model="form.name" placeholder="模板名称" /></el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item v-if="!shareBackgroundOnly" label="分类" prop="type">
            <el-select v-model="form.type" placeholder="选择分类">
              <el-option label="每日一记模板" value="dailyDiary" />
              <el-option label="情绪感悟模板" value="emotion" />
              <el-option label="成长手记模板" value="growthHandnote" />
              <el-option label="简约海报模板" value="simplePoster" />
              <el-option label="治愈卡片模板" value="healingCard" />
            </el-select>
          </el-form-item>
          <el-form-item v-else label="分类"><el-tag type="success">分享背景图</el-tag></el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item v-if="!shareBackgroundOnly" label="场景" prop="scene">
            <el-select v-model="form.scene" placeholder="选择场景">
              <el-option label="日记分享" value="diaryShare" />
              <el-option label="朋友圈分享" value="momentsShare" />
              <el-option label="知识卡片" value="knowledgeCard" />
            </el-select>
          </el-form-item>
          <el-form-item v-else label="场景"><el-tag>日记分享</el-tag></el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="适用身份">
            <el-radio-group v-model="form.vipOnly">
              <el-radio :label="0">全体</el-radio>
              <el-radio :label="1">仅会员</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="启用">
            <el-switch v-model="form.isEnabled" :active-value="1" :inactive-value="0" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="6"><el-form-item label="画布宽"><el-input-number v-model="form.canvasWidth" :min="300" :max="2000" /></el-form-item></el-col>
        <el-col :span="6"><el-form-item label="画布高"><el-input-number v-model="form.canvasHeight" :min="400" :max="3000" /></el-form-item></el-col>
        <el-col :span="6"><el-form-item label="背景色"><el-color-picker v-model="form.bgColor" /></el-form-item></el-col>
        <el-col :span="6"><el-form-item label="排序"><el-input-number v-model="form.sortOrder" :min="0" :max="999" /></el-form-item></el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="字体"><el-input v-model="form.fontFamily" placeholder="默认字体" /></el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="封面预览图">
            <el-upload
              :action="uploadAction"
              :before-upload="beforeUpload"
              :file-list="previewFileList"
              list-type="picture-card"
              :on-success="onPreviewSuccess"
              :on-remove="handlePreviewRemove"
              :limit="1"
              :http-request="httpRequestPreview"
              accept="image/*"
            >
              <i class="el-icon-plus"></i>
            </el-upload>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="背景图">
            <el-upload
              :action="uploadAction"
              :before-upload="beforeUpload"
              :file-list="bgFileList"
              list-type="picture-card"
              :on-success="onBgSuccess"
              :on-remove="handleBgRemove"
              :limit="1"
              :http-request="httpRequestBg"
              accept="image/*"
            >
              <i class="el-icon-plus"></i>
            </el-upload>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="Dify生成提示词">
        <el-input
          v-model="form.difyPrompt"
          type="textarea"
          :rows="5"
          maxlength="700"
          show-word-limit
          placeholder="此处是该模板实际生图的风格提示词。留空时自动使用同名内置风格；保存后以此处内容优先。"
        />
        <div style="color:#909399;font-size:12px;line-height:20px;">请描述风格、配色、材质和排版，不要写固定标题、人物、楼梯或具体故事；动态正文会由系统自动注入。</div>
      </el-form-item>

      <!-- ====== 动态元素列表 ====== -->
      <el-divider content-position="left">
        画布元素
        <el-button type="primary" size="mini" icon="el-icon-plus" style="margin-left:12px" @click="addElement">添加元素</el-button>
      </el-divider>

      <el-table :data="elements" border size="small" style="margin-bottom:20px">
        <el-table-column label="序号" width="50" align="center">
          <template slot-scope="{ $index }">{{ $index + 1 }}</template>
        </el-table-column>
        <el-table-column label="类型" width="110">
          <template slot-scope="{ row }">
            <el-select v-model="row.type" size="mini" @change="onElementTypeChange(row)">
              <el-option label="文字" value="text" />
              <el-option label="图片" value="image" />
              <el-option label="小程序码" value="qrcode" />
              <el-option label="背景图" value="background" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="来源" width="150">
          <template slot-scope="{ row }">
            <el-select v-if="row.type === 'text'" v-model="row.source" size="mini" placeholder="数据源">
              <el-option label="标题" value="title" />
              <el-option label="金句" value="quote" />
              <el-option label="正文" value="content" />
              <el-option label="日期" value="date" />
              <el-option label="标语" value="slogan" />
              <el-option label="标签" value="tags" />
              <el-option label="自定义" value="custom" />
            </el-select>
            <el-select v-else-if="row.type === 'image'" v-model="row.source" size="mini" placeholder="图片来源">
              <el-option label="海报图" value="posterImage" />
              <el-option label="用户上传图" value="userImage" />
              <el-option label="封面图" value="coverImage" />
              <el-option label="小程序码" value="qrCode" />
              <el-option label="Logo" value="logo" />
              <el-option label="自定义URL" value="customUrl" />
            </el-select>
            <span v-else-if="row.type === 'qrcode'" style="font-size:12px;color:#999">自动生成</span>
            <el-select v-else-if="row.type === 'background'" v-model="row.source" size="mini" placeholder="背景来源">
              <el-option label="模板背景图" value="bgUrl" />
              <el-option label="纯色背景" value="bgColor" />
              <el-option label="自定义URL" value="customUrl" />
            </el-select>
            <span v-else style="font-size:12px;color:#999">-</span>
            <el-input 
              v-if="row.source === 'customUrl'" 
              v-model="row.customUrl" 
              size="mini" 
              placeholder="请输入图片URL" 
              style="margin-top:4px"
            />
          </template>
        </el-table-column>
        <el-table-column label="自定义文字" width="120">
          <template slot-scope="{ row }">
            <el-input v-if="row.type === 'text' && row.source === 'custom'" v-model="row.customText" size="mini" placeholder="输入文字" />
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="X" width="70"><template slot-scope="{ row }"><el-input v-model="row.x" size="mini" placeholder="0" /></template></el-table-column>
        <el-table-column label="Y" width="70"><template slot-scope="{ row }"><el-input v-model="row.y" size="mini" placeholder="0" /></template></el-table-column>
        <el-table-column label="宽" width="70">
          <template slot-scope="{ row }">
            <el-input v-if="row.type !== 'text'" v-model="row.width" size="mini" placeholder="0" />
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="高" width="70">
          <template slot-scope="{ row }">
            <el-input v-if="row.type !== 'text'" v-model="row.height" size="mini" placeholder="0" />
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="最大宽" width="70">
          <template slot-scope="{ row }">
            <el-input v-if="row.type === 'text'" v-model="row.maxWidth" size="mini" placeholder="0" />
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="字号" width="65">
          <template slot-scope="{ row }">
            <el-input v-if="row.type === 'text'" v-model="row.fontSize" size="mini" placeholder="16" />
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="行高" width="65">
          <template slot-scope="{ row }">
            <el-input v-if="row.type === 'text'" v-model="row.lineHeight" size="mini" placeholder="24" />
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="最大行" width="65">
          <template slot-scope="{ row }">
            <el-input v-if="row.type === 'text'" v-model="row.maxLines" size="mini" placeholder="2" />
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="颜色" width="60">
          <template slot-scope="{ row }">
            <el-color-picker v-if="row.type === 'text'" v-model="row.color" size="mini" />
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="对齐" width="80">
          <template slot-scope="{ row }">
            <el-select v-if="row.type === 'text'" v-model="row.align" size="mini">
              <el-option label="左" value="left" />
              <el-option label="中" value="center" />
              <el-option label="右" value="right" />
            </el-select>
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="基线" width="80">
          <template slot-scope="{ row }">
            <el-select v-if="row.type === 'text'" v-model="row.baseline" size="mini" placeholder="top">
              <el-option label="顶部" value="top" />
              <el-option label="中间" value="middle" />
              <el-option label="底部" value="bottom" />
            </el-select>
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="圆角" width="65">
          <template slot-scope="{ row }">
            <el-input v-if="row.type === 'image' || row.type === 'qrcode'" v-model="row.radius" size="mini" placeholder="0" />
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="填充" width="80">
          <template slot-scope="{ row }">
            <el-select v-if="row.type === 'image'" v-model="row.objectFit" size="mini" placeholder="cover">
              <el-option label="覆盖" value="cover" />
              <el-option label="包含" value="contain" />
              <el-option label="拉伸" value="fill" />
            </el-select>
            <span v-else style="font-size:12px;color:#ccc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="层级" width="60">
          <template slot-scope="{ row }"><el-input v-model="row.zIndex" size="mini" placeholder="1" /></template>
        </el-table-column>
        <el-table-column label="操作" width="70" fixed="right">
          <template slot-scope="{ $index }">
            <el-button type="text" icon="el-icon-delete" style="color:#f56c6c" @click="removeElement($index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-alert title="前端渲染规则" type="info" :closable="false" style="margin-top:8px">
        前端按元素顺序依次绘制。文字类型根据source取值（title/quote/content/date/slogan/tags/customText）。
        图片根据source取对应URL。小程序码前端自动生成。背景图优先绘制。
      </el-alert>
    </el-form>
  </GlobalWindow>
</template>
<script>
import BaseOpera from '@/components/base/BaseOpera'
import GlobalWindow from '@/components/common/GlobalWindow'
import request from '@/utils/request'

const DEFAULT_ELEMENTS = [
  { type: 'background', source: 'bgColor', x: '0', y: '0', width: '', height: '', zIndex: '0' },
  { type: 'image', source: 'posterImage', x: '0', y: '0', width: '720', height: '690', radius: '0', objectFit: 'cover', zIndex: '0' },
  { type: 'text', source: 'quote', x: '360', y: '88', maxWidth: '430', fontSize: '38', lineHeight: '48', maxLines: '2', color: '#ffffff', align: 'center', baseline: 'top', zIndex: '1' },
  { type: 'text', source: 'title', x: '40', y: '730', maxWidth: '550', fontSize: '68', lineHeight: '76', maxLines: '2', color: '#1f1a17', align: 'left', baseline: 'top', zIndex: '2' },
  { type: 'text', source: 'content', x: '40', y: '920', maxWidth: '540', fontSize: '22', lineHeight: '34', maxLines: '3', color: '#6d665f', align: 'left', baseline: 'top', zIndex: '3' },
  { type: 'image', source: 'qrCode', x: '550', y: '900', width: '132', height: '132', radius: '0', objectFit: 'cover', zIndex: '5' },
  { type: 'image', source: 'logo', x: '558', y: '1050', width: '116', height: '34', radius: '0', objectFit: 'cover', zIndex: '6' },
  { type: 'text', source: 'slogan', x: '606', y: '1094', maxWidth: '300', fontSize: '18', lineHeight: '24', maxLines: '1', color: '#8a837c', align: 'center', baseline: 'top', zIndex: '7' }
]

export default {
  name: 'operaposterTemplateWindow',
  extends: BaseOpera,
  components: { GlobalWindow },
  props: {
    width: { type: String, default: '80%' },
    shareBackgroundOnly: { type: Boolean, default: false }
  },
  data() {
    return {
      elements: JSON.parse(JSON.stringify(DEFAULT_ELEMENTS)),
      form: {
        id: null, scene: 'momentsShare', type: 'dailyDiary', name: '',
        previewUrl: '', bgUrl: '', bgColor: '#f5f2ec',
        canvasWidth: 720, canvasHeight: 1120,
        difyPrompt: '', fontFamily: '', vipOnly: 0, sortOrder: 0, isEnabled: 1,
        layoutJson: '', layout: { elements: [] }
      },
      rules: {
        name: [{ required: true, message: '请输入模板名称', trigger: 'blur' }],
        scene: [{ required: true, message: '请选择场景', trigger: 'change' }],
        type: [{ required: true, message: '请选择分类', trigger: 'change' }]
      },
      imgUrl: process.env.VUE_APP_API_IMG,
      uploadAction: '',
      previewFileList: [],
      bgFileList: []
    }
  },
  methods: {
    beforeUpload(file) {
      if (file.size / 1024 / 1024 > 5) {
        this.$message.warning('上传图片大小不能超过5MB')
        return false
      }
      return true
    },
    
    async httpRequestPreview(file) {
      const formData = new FormData()
      formData.append('file', file.file)
      try {
        const response = await request.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        response.success = true
        response.data = response
        this.onPreviewSuccess(response, file)
      } catch (error) {
        console.log(error)
        this.$message.error('文件上传失败')
      }
    },
    
    onPreviewSuccess(res) {
      if (!res.success) { this.$message.error('文件上传失败'); return }
      this.form.previewUrl = res.data.accessUri
      this.previewFileList = [{ name: res.data.accessUri, url: this.imgUrl + res.data.accessUri, accessUri: res.data.accessUri }]
    },
    
    handlePreviewRemove() { this.form.previewUrl = ''; this.previewFileList = [] },
    
    async httpRequestBg(file) {
      const formData = new FormData()
      formData.append('file', file.file)
      try {
        const response = await request.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        response.success = true
        response.data = response
        this.onBgSuccess(response, file)
      } catch (error) {
        console.log(error)
        this.$message.error('文件上传失败')
      }
    },
    
    onBgSuccess(res) {
      if (!res.success) { this.$message.error('文件上传失败'); return }
      this.form.bgUrl = res.data.accessUri
      this.bgFileList = [{ name: res.data.accessUri, url: this.imgUrl + res.data.accessUri, accessUri: res.data.accessUri }]
    },
    
    handleBgRemove() { this.form.bgUrl = ''; this.bgFileList = [] },
    
    addElement() {
      this.elements.push({
        type: 'text', source: 'custom', customText: '', x: '0', y: '0',
        maxWidth: '200', fontSize: '16', lineHeight: '24', maxLines: '1',
        color: '#333333', align: 'left', baseline: 'top', zIndex: '1'
      })
    },
    removeElement(index) { this.elements.splice(index, 1) },
    
    onElementTypeChange(row) {
      if (row.type === 'text') {
        if (!row.source) row.source = 'custom'
        if (!row.fontSize) row.fontSize = '16'
        if (!row.lineHeight) row.lineHeight = '24'
        if (!row.maxLines) row.maxLines = '1'
        if (!row.color) row.color = '#333333'
        if (!row.align) row.align = 'left'
        if (!row.baseline) row.baseline = 'top'
      }
      if (row.type === 'image') {
        if (!row.width) row.width = '100'
        if (!row.height) row.height = '100'
        if (!row.objectFit) row.objectFit = 'cover'
        if (!row.source) row.source = 'posterImage'
      }
      if (row.type === 'qrcode') {
        if (!row.width) row.width = '100'
        if (!row.height) row.height = '100'
      }
      if (row.type === 'background') {
        if (!row.source) row.source = 'bgColor'
      }
      if (!row.zIndex) row.zIndex = '1'
    },
    
    handleConfirm() {
      this.form.layout = { elements: this.elements }
      this.form.layoutJson = JSON.stringify(this.form.layout)
      this.confirm()
    },
    
    open(title, row) {
      this.previewFileList = []
      this.bgFileList = []
      
      if (row) {
        this.form = { ...this.form, ...row }
        if (this.shareBackgroundOnly) {
          this.form.scene = 'diaryShare'
          this.form.type = 'shareBackground'
        }
        if (row.previewUrl) {
          this.previewFileList = [{ name: row.previewUrl, url: this.imgUrl + row.previewUrl, accessUri: row.previewUrl }]
        }
        if (row.bgUrl) {
          this.bgFileList = [{ name: row.bgUrl, url: this.imgUrl + row.bgUrl, accessUri: row.bgUrl }]
        }
        if (row.layoutJson) {
          try {
            const layout = typeof row.layoutJson === 'string' ? JSON.parse(row.layoutJson) : row.layoutJson
            if (layout.elements && Array.isArray(layout.elements)) {
              this.elements = layout.elements
            }
          } catch (e) {
            this.elements = JSON.parse(JSON.stringify(DEFAULT_ELEMENTS))
          }
        }
      } else {
        this.form = {
          id: null, scene: this.shareBackgroundOnly ? 'diaryShare' : 'momentsShare', type: this.shareBackgroundOnly ? 'shareBackground' : 'dailyDiary', name: '',
          previewUrl: '', bgUrl: '', bgColor: '#f5f2ec',
          canvasWidth: 720, canvasHeight: 1120,
          difyPrompt: '', fontFamily: '', vipOnly: 0, sortOrder: 0, isEnabled: 1,
          layoutJson: '', layout: { elements: [] }
        }
        this.elements = JSON.parse(JSON.stringify(DEFAULT_ELEMENTS))
      }
      this.title = title
      this.visible = true
    }
  },
  created() { this.config({ api: '/rentHouse/posterTemplate', 'field.id': 'id' }) }
}
</script>
